'use client';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Select, Row, Col, SelectProps, Collapse, ConfigProvider } from 'antd';
import { PlusOutlined, MinusOutlined, CloseCircleFilled } from '@ant-design/icons';
import cn from 'classnames';
import moment from 'moment';
import Masonry from 'react-masonry-css';
import { useRouter, usePathname } from 'next/navigation';
import PhotoList from './PhotoList';
import Text from '../Text';
import Heading from '../Heading';
import ImagePreview, { ImagePreviewGroup } from '../ImagePreview';
import {
    PagePhotoAlbumBlockFragment,
    DateListFragment,
    PageRowFragment,
} from '@/@types/generated/sitecore';
import { DefaultOptionType } from 'antd/es/select';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import { WATrackerClickEvn, WATrackerTrackPageView } from '@/utils/wa';
import ArrowUpIcon from '@public/images/common/select_arrow_up.svg';
import ArrowDownIcon from '@public/images/common/select_arrow_down.svg';
import style from './index.module.scss';
import withTheme from '@/theme';
import useWindowSize from '@/utils/useScreenSize';
import { mobileBreakPoint, withFullPath } from '@/utils/utils';
import { useTranslation } from '@/i18n/use-transition.client';
import RichText from '../RichText';
import useALTracker, { EventClickType } from '@/utils/useALTracker';

export type PhotoType = {
    name: string;
    albumDate?: { value?: string };
    albumImage?: { src?: string };
    albumCaption?: { value?: string };
    dateList: DateListFragment[];
    albumSelection: { targetItems: { url: string }[] };
    partyName?: string;
    partyItemName?: string;
    enabled?: { boolValue?: boolean };
};

type PartyInfo = {
    name: string;
    albumList: {
        name: string;
        photoList: Array<PhotoType>;
    }[];
    id?: string;
    partyName?:
        | {
              value?: string | undefined;
          }
        | undefined;
};

type ComponentPropsType = {
    handlePartySelectClick: (value: any, option: DefaultOptionType | DefaultOptionType[]) => void;
    mainList: {
        value: string;
        label: string;
        key: string;
    }[];
    handleDateSelectClick: (value: any, option: DefaultOptionType | DefaultOptionType[]) => void;
    setPartySelected: (value: SetStateAction<string | undefined>) => void;
    setDateSelected: (value: SetStateAction<string | undefined>) => void;
    subSelectOptions: DefaultOptionType[];
    photoList: PhotoType[];
    handleImageClick: (item: PhotoType) => void;
    setCurrentIndex: Dispatch<SetStateAction<PAGE_INDEX>>;
    showDateItemKeys: string[];
    filterData: (items: PhotoType[]) => {
        key: string;
        label: JSX.Element;
        children: JSX.Element;
    }[];
    partyDetailList: PhotoType[];
    setShowDateItemKeys: Dispatch<SetStateAction<string[]>>;
    partySelected?: string;
    dateSelected?: string;
    currentDateItem?: PhotoType;
    fromDetail?: boolean;
    description?: PageRowFragment;
};

const pageList = [
    {
        name: 'PartyReview',
    },
    { name: 'PartyDetail' },
];

export enum PAGE_INDEX {
    LIST,
    DETAIL,
}

const PartyReview = (props: ComponentPropsType) => {
    const {
        description,
        handlePartySelectClick,
        mainList,
        partySelected,
        setPartySelected,
        setDateSelected,
        handleDateSelectClick,
        subSelectOptions,
        dateSelected,
        photoList,
        handleImageClick,
    } = props;
    const { t } = useTranslation();
    const [isMainSelectOpen, setIsMainSelectOpen] = useState(false);
    const [isSubSelectOpen, setIsSubSelectOpen] = useState(false);
    // const isMobile = useWindowSize().width < mobileBreakPoint;
    return withTheme(
        <>
            <Row gutter={[20, 40]}>
                <Col xs={24} lg={12}>
                    <RichText html={description?.rowContent?.rendered || ''} />
                </Col>
                <Col xs={24} lg={6}>
                    <Select
                        className='w-full'
                        onChange={handlePartySelectClick}
                        allowClear={{
                            clearIcon: (
                                <CloseCircleFilled
                                    onClick={() => {
                                        setPartySelected(undefined);
                                    }}
                                />
                            ),
                        }}
                        options={mainList}
                        value={partySelected}
                        placeholder={t('albumSelectPartyPlaceholder')}
                        popupClassName={style.select}
                        suffixIcon={isMainSelectOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                        onDropdownVisibleChange={setIsMainSelectOpen}
                    />
                </Col>
                <Col xs={24} lg={6}>
                    <Select
                        className='w-full'
                        onChange={handleDateSelectClick}
                        allowClear={{
                            clearIcon: (
                                <CloseCircleFilled
                                    onClick={() => {
                                        setDateSelected(undefined);
                                    }}
                                />
                            ),
                        }}
                        options={subSelectOptions}
                        value={dateSelected}
                        placeholder={t('albumSelectDatePlaceholder')}
                        popupClassName={style.select}
                        suffixIcon={isSubSelectOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                        onDropdownVisibleChange={setIsSubSelectOpen}
                    />
                </Col>
            </Row>
            <Row gutter={[0, 40]} className={cn('w-full mt-16', style.selectWrap)}>
                <Col span={24}>
                    <PhotoList photoList={photoList} handleImageClick={handleImageClick} />
                </Col>
            </Row>
        </>,
        {
            token: {
                colorText: '#555',
                colorTextPlaceholder: '#555',
            },
        },
    );
};

const PartyDetail = (props: ComponentPropsType) => {
    const {
        setCurrentIndex,
        currentDateItem,
        showDateItemKeys,
        filterData,
        partyDetailList,
        setShowDateItemKeys,
        fromDetail,
    } = props;
    const router = useRouter();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();
    const themeColorCode = globalStore.currentTheme?.themeColor;
    const lang = globalStore.lang;
    const isMobile = useWindowSize().width < mobileBreakPoint;
    const { ALTrackClickEvent } = useALTracker();
    return (
        <>
            <div
                onClick={() => {
                    if (fromDetail) {
                        fromDetail && router.push(`/${lang}/album`);
                    } else {
                        setCurrentIndex(PAGE_INDEX.LIST);
                    }
                }}
            >
                <Text
                    level='subtitle1'
                    className='text-slate-200 cursor-pointer'
                    text={t('albumBack')}
                />
            </div>
            <Row gutter={[0, 40]} className={cn('w-full mt-10')}>
                <Col span={24}>
                    <Heading
                        level={3}
                        className='text-base-font-color text-center'
                        text={currentDateItem?.partyName}
                        style={{ color: '#FFFFFF' }}
                    />
                </Col>
                <Col span={24}>
                    <ConfigProvider
                        theme={{
                            token: {
                                colorBorder: '#36536F;',
                                colorTextHeading: '#fff',
                                colorText: '#fff',
                            },
                            components: {
                                Collapse: {
                                    headerBg: 'transparent',
                                    headerPadding: isMobile
                                        ? '37px 0px 17px 0px'
                                        : '37px 2px 17px 0px',
                                    contentBg: 'transparent',
                                    contentPadding: isMobile
                                        ? '22px 0px 17px 0px'
                                        : '22px 2px 17px 0px',
                                },
                            },
                        }}
                    >
                        <Collapse
                            activeKey={showDateItemKeys}
                            expandIconPosition={'end'}
                            bordered={false}
                            collapsible='header'
                            onChange={(key) => {
                                if (key instanceof Array) {
                                    setShowDateItemKeys(key);
                                    if (key.length > showDateItemKeys.length) {
                                        ALTrackClickEvent(
                                            `RnE/HW_Click-Expand-${currentDateItem?.partyName}_Album-Accordion_${moment(key[key.length - 1]).format('DD/MM/YYYY')}`,
                                        );
                                        // WATrackerClickEvn(
                                        //     `RnE/HW_Click-Expand-${currentDateItem?.partyName}_${moment(key[key.length - 1]).format('DD/MM/YYYY')}`,
                                        // );
                                    }
                                } else {
                                    setShowDateItemKeys([key]);
                                }
                            }}
                            expandIcon={(expandProps: any) => {
                                const { isActive, panelKey } = expandProps;
                                return isActive ? (
                                    <MinusOutlined
                                        style={{ fontSize: 22, color: themeColorCode }}
                                    />
                                ) : (
                                    <PlusOutlined style={{ fontSize: 22, color: themeColorCode }} />
                                );
                            }}
                            items={filterData(partyDetailList)}
                        />
                    </ConfigProvider>
                </Col>
            </Row>
        </>
    );
};

const Page = {
    PartyReview,
    PartyDetail,
};

const PagePhotoAlbumBlock = (props: {
    description?: PageRowFragment;
    content: PagePhotoAlbumBlockFragment;
    currenIndex?: PAGE_INDEX;
    party?: string;
    date?: string;
}) => {
    const { content, party, date, currenIndex, description } = props;
    const [subSelectOptions, setSubSelectOptions] = useState<DefaultOptionType[]>([]);
    const [partySelected, setPartySelected] = useState<string>();
    const [dateSelected, setDateSelected] = useState<string>();
    const [photoList, setPhotoList] = useState<PhotoType[]>([]);

    const [currentIndex, setCurrentIndex] = useState(currenIndex || PAGE_INDEX.LIST);
    const [showDateItemKeys, setShowDateItemKeys] = useState<string[]>([]);
    const [partyDetailList, setPartyDetailList] = useState<PhotoType[]>([]);
    const [currentDateItem, setCurrentDateItem] = useState<PhotoType>();
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number | undefined>();
    const partyList = content.parties?.partyList as PartyInfo[];

    const router = useRouter();
    const pathname = usePathname();
    const globalStore = useGlobalStore();
    const { ALTrackPageLoad, ALTrackClickEvent } = useALTracker();

    const initPhotoList = () => {
        const defaultPhotoList = partyList
            ?.reduce((prev, current) => {
                if (current.albumList && current.albumList[0]) {
                    const currentPhotoList = current.albumList[0].photoList.map((item) => ({
                        ...item,
                        partyName: current.partyName?.value,
                        partyItemName: current.name,
                    }));
                    return [...prev, ...currentPhotoList];
                }
                return prev;
            }, [] as PhotoType[])
            .filter((i) => i.enabled?.boolValue);
        const dateList = defaultPhotoList.map((item, index) => {
            return {
                value: `${item.partyName}/${moment(item.albumDate?.value || '').format('yyyyMMDD') || ''}`,
                label: `${moment(item.albumDate?.value || '').format('DD/MM/yyyy') || ''}  ${item.partyName}`,
                key: item.partyName + (item.albumDate?.value || '') + index,
            };
        });
        setPhotoList(
            defaultPhotoList.sort(
                (a, b) =>
                    moment(b.albumDate?.value || '').valueOf() -
                    moment(a.albumDate?.value || '').valueOf(),
            ),
        );
        setSubSelectOptions(dateList);
    };

    useEffect(() => {
        initPhotoList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mainList = partyList?.map((item) => {
        return {
            value: item?.partyName?.value || '',
            label: item?.partyName?.value || '',
            key: item?.partyName?.value || '',
        };
    });

    const handlePartySelectClick: SelectProps['onChange'] = (e) => {
        const targetParty = partyList?.find((i) => i.partyName?.value === e);
        setPartySelected(e);
        setDateSelected(undefined);
        if (!e) {
            initPhotoList();
            setDateSelected(undefined);
            return;
        }
        if (targetParty) {
            // ALTrackClickEvent(`RnE/HW_Click-Filter-${e}_Filter-menu_${dateSelected || ''}`);
            // WATrackerClickEvn(`RnE/HW_Click-Filter-${e}_Filter-menu_${dateSelected || ''}`);
            if (!targetParty.albumList[0]) {
                setPhotoList([]);
                setSubSelectOptions([]);
                return;
            }
            const dateList = targetParty.albumList[0].photoList.map((item, index) => {
                return {
                    value: `${targetParty.partyName?.value}/${moment(item.albumDate?.value || '').format('yyyyMMDD') || ''}`,
                    label: `${moment(item.albumDate?.value || '').format('DD/MM/yyyy') || ''} ${targetParty.partyName?.value}`,
                    key: targetParty.partyName?.value + (item.albumDate?.value || '') + index,
                };
            });
            setPhotoList(
                targetParty.albumList[0].photoList
                    .map((item) => ({
                        ...item,
                        partyName: targetParty.partyName?.value,
                        partyItemName: targetParty.name,
                    }))
                    .filter((i) => i.enabled?.boolValue)
                    .sort(
                        (a, b) =>
                            moment(b.albumDate?.value || '').valueOf() -
                            moment(a.albumDate?.value || '').valueOf(),
                    ),
            );
            setSubSelectOptions(dateList);
        }
    };

    const handleDateSelectClick: SelectProps['onChange'] = (e) => {
        const noPartySelect = !partySelected;
        const selectedParty = e ? e.split('/')[0] : null;
        const selectedDate = e ? e.split('/')[1] : null;
        const targetParty = partyList?.find(
            (i) => i.partyName?.value === (selectedParty || partySelected),
        );
        setDateSelected(e);

        if (targetParty) {
            if (!e) {
                setPhotoList(
                    targetParty.albumList[0].photoList
                        .map((item) => ({
                            ...item,
                            partyName: targetParty.partyName?.value,
                            partyItemName: targetParty.name,
                        }))
                        .filter((i) => i.enabled?.boolValue)
                        .sort(
                            (a, b) =>
                                moment(b.albumDate?.value || '').valueOf() -
                                moment(a.albumDate?.value || '').valueOf(),
                        ),
                );
                return;
            }
            ALTrackClickEvent(
                `RnE/HW_Click-Filter-${selectedParty}_Filter-menu_${moment(selectedDate).format('DD/MM/YYYY')}`,{
                    _pageId: 'RnE/HW/album/Home'
                }
            );
            // WATrackerClickEvn(
            //     `RnE/HW_Click-Filter-${selectedParty}_Filter-menu_${moment(selectedDate).format('DD/MM/YYYY')}`,
            // );
            if (!targetParty.albumList[0]) {
                setSubSelectOptions([]);
                return;
            }
            if (noPartySelect || partySelected !== selectedParty) {
                setPartySelected(targetParty.partyName?.value);
            }
            const datePhotoList = targetParty.albumList[0].photoList
                .reduce((prev, current) => {
                    if (current.name === selectedDate && current.enabled?.boolValue) {
                        return [
                            ...prev,
                            {
                                ...current,
                                partyName: targetParty.partyName?.value,
                                partyItemName: targetParty.name,
                            },
                        ];
                    }
                    return prev;
                }, [] as PhotoType[])
                .sort(
                    (a, b) =>
                        moment(b.albumDate?.value || '').valueOf() -
                        moment(a.albumDate?.value || '').valueOf(),
                );
            setPhotoList(datePhotoList);
        }
    };

    const handleImageClick = (item: PhotoType) => {
        ALTrackClickEvent('RnE/HW_ClickBanner_Album-listing', {
            eventType: EventClickType.clickBanner,
            internal_ad: `SPLDSP_RnE/HW/${globalStore.pageID}_Album-listing`
        });
        // WATrackerClickEvn('RnE/HW_ClickBanner_Album-listing');
        router.push(
            `${item.partyItemName}/${moment(item.albumDate?.value).format('YYYYMMDD')}/?b_cid=SPLDSP_RnE/HW/${globalStore.pageID}_Album-listing`,
        );
    };

    const filterData = (items: PhotoType[]) => {
        return (
            items &&
            items
                .filter((i) => i.enabled?.boolValue)
                .map((item) => {
                    const imageList = item?.albumSelection
                        ? item?.albumSelection?.targetItems?.map((img, index) => ({
                              src: withFullPath(img.url),
                              mediaType: 'Photo',
                              videoSrc: '',
                              mediaCaption: '',
                              id: index,
                              videoOptions: {},
                          }))
                        : item?.dateList?.map((img, index) => ({
                              src: withFullPath(img.contentImage?.src),
                              mediaType: img.mediaType?.value,
                              videoSrc: img.videoSrc?.value,
                              mediaCaption: img.mediaCaption?.value,
                              id: index,
                              videoOptions: {},
                          }));
                    return {
                        key: moment(item.albumDate?.value).format('YYYYMMDD'),
                        label: (
                            <Text
                                level={'body2'}
                                className='text-base-font-color font-extrabold'
                                text={moment(item.albumDate?.value).format('DD/MM/YYYY')}
                            />
                        ),
                        children: (
                            <ImagePreviewGroup
                                imageList={imageList}
                                canPreview
                                total={(imageList || []).length}
                                currentPreviewIndex={currentPreviewIndex}
                                handleVisibleChange={(visible) => {
                                    if (!visible) {
                                        setCurrentPreviewIndex(undefined);
                                    }
                                }}
                            >
                                <Masonry
                                    breakpointCols={{
                                        default: 4,
                                        1024: 3,
                                        768: 1,
                                        480: 1,
                                    }}
                                    className='flex gap-6'
                                    columnClassName='grid-item'
                                >
                                    {imageList.map((dateItem, index) => {
                                        return (
                                            <div
                                                className='mb-4'
                                                key={dateItem.id}
                                                onClick={() => {
                                                    setCurrentPreviewIndex(index);
                                                }}
                                            >
                                                <ImagePreview
                                                    key={dateItem.id}
                                                    setting={{
                                                        src: dateItem?.src,
                                                        className: 'rounded-[5px]',
                                                        rootClassName: 'w-full',
                                                    }}
                                                    mediaOption={{
                                                        mediaType: dateItem.mediaType as
                                                            | 'Video'
                                                            | 'Photo'
                                                            | undefined,
                                                    }}
                                                    canPreview
                                                />
                                            </div>
                                        );
                                    })}
                                </Masonry>
                            </ImagePreviewGroup>
                        ),
                    };
                })
        );
    };

    useEffect(() => {
        if (party || date) {
            const targetParty = partyList.find((i) => i.name === party || i.id === party);
            const targetPhotoList =
                targetParty && targetParty.albumList && targetParty.albumList[0].photoList;
            if (targetParty && targetParty.albumList[0]) {
                const partyDetailSortList = targetParty.albumList[0].photoList
                    .map((item) => ({
                        ...item,
                        partyName: targetParty.partyName?.value,
                        partyItemName: targetParty.name,
                    }))
                    .sort(
                        (a, b) =>
                            moment(b.albumDate?.value || '').valueOf() -
                            moment(a.albumDate?.value || '').valueOf(),
                    );
                setPartyDetailList(partyDetailSortList);
                setShowDateItemKeys([
                    moment(partyDetailSortList[0].albumDate?.value).format('YYYYMMDD'),
                ]);
            }
            if (party && date) {
                const dateItem = targetPhotoList?.find(
                    (i) => moment(i.albumDate?.value).format('YYYYMMDD') === date,
                );
                if (dateItem) {
                    dateItem.partyName = targetParty && targetParty.partyName?.value;
                    dateItem.partyItemName = party;
                }
                setShowDateItemKeys([date]);
                setCurrentDateItem(dateItem);
                const pageID = pathname.replace(`/${globalStore.lang}/`, '').replace(/\/$/g, '');
                globalStore.setGlobalStore({
                    pageID,
                });
                // WATrackerTrackPageView(`RnE/HW/${pageID}`);
                // ALTrackPageLoad(`RnE/HW/${pageID}`);
            }
        }
    }, [party, date, partyList, globalStore.lang]);

    const componentProps: ComponentPropsType = {
        description,
        handlePartySelectClick,
        mainList,
        partySelected,
        setPartySelected,
        setDateSelected,
        handleDateSelectClick,
        subSelectOptions,
        dateSelected,
        photoList,
        handleImageClick,
        setCurrentIndex,
        currentDateItem,
        showDateItemKeys,
        filterData,
        partyDetailList,
        setShowDateItemKeys,
        fromDetail: Boolean(party || date),
    };

    return (
        <>
            {pageList.map((item, index) => {
                const Component = Page[item.name as keyof typeof Page];
                return index === currentIndex && <Component key={item.name} {...componentProps} />;
            })}
        </>
    );
};

export default PagePhotoAlbumBlock;
